import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { Router, RouterModule } from '@angular/router';
import { CommonDirectives } from '@app/shared/directives/common-directives';
import { UserAuthService } from '../../services/userAuth.service';
import { LoadSpinnerWrapperComponent } from "../../../../shared/components/load-spinner-wrapper/load-spinner-wrapper.component";
import { AppState } from '@app/core/store/app.state';
import { Store } from '@ngrx/store';
import * as UserAuthActions from '@app/core/store/userAuth/userAuth.actions';
import { Subject } from 'rxjs';
import {ToastService} from "@app/core/services/toast.service";
import { PasswordResetStateService } from '../../services/password-reset-state.service';

@Component({
  selector: 'app-portal-login',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    CommonDirectives,
    RouterModule,
    LoadSpinnerWrapperComponent
],
  templateUrl: './portal-login-reset-password.component.html',
  styleUrl: './portal-login-reset-password.component.scss'
})
export class PortalLoginResetPasswordComponent implements OnInit, OnDestroy {
  ngDestroy$ = new Subject();
  loading = false;
  loginForm = this.fb.group({
    code: ['', Validators.required],
    password: [
      '',
      [
        Validators.required,
        Validators.minLength(10),
        Validators.pattern(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\^$*.\[\]{}()?"!@#%&/\\,><':;|_~`+=-]).{10,}$/)
      ]
    ],
    confirmPassword: ['', Validators.required]
  });
  identifier = '';
  resendCodeAttempt = false;

  constructor(
    private fb: FormBuilder,
    private userAuthService: UserAuthService,
    private store: Store<AppState>,
    private router: Router,
    private toastService: ToastService,
    private passwordResetState: PasswordResetStateService
  ) {
    const nav = this.router.getCurrentNavigation();
    const navigationIdentifier = nav?.extras?.state?.['identifier'];
    this.identifier = navigationIdentifier ?? this.passwordResetState.get();

    if (navigationIdentifier) {
      this.passwordResetState.save(navigationIdentifier);
    }
  }

  ngOnInit(): void {
    if (!this.identifier) {
      this.toastService.showErrorToast('Your password reset session has expired. Please request a new code.');
      this.router.navigate(['/forgot-password']);
    }
  }

  ngOnDestroy(): void {
    this.ngDestroy$.next(true);
  }

  resendCode() {
    if (!this.identifier || this.resendCodeAttempt) {
      return;
    }

    this.resendCodeAttempt = true;
    this.userAuthService.forgotPassword(this.identifier)
      .subscribe({
        next: (response) => {
          if (!response.succeeded) {
            this.toastService.showErrorToast(`We couldn't resend the code. Please try again.`);
          } else {
            this.toastService.showSuccessToast(`A new code has been sent. Check your email.`);
          }
          this.resendCodeAttempt = false;
        },
        error: () => {
          this.resendCodeAttempt = false;
          this.toastService.showErrorToast(`We couldn't resend the code. Please try again.`);
        }
      });
  }

  changePassword() {
    if (!this.identifier) {
      this.toastService.showErrorToast('Your password reset session has expired. Please request a new code.');
      this.router.navigate(['/forgot-password']);
      return;
    }

    if (this.loginForm.invalid) {
      this.loginForm.markAllAsTouched();
      return;
    }

    const { code, password, confirmPassword } = this.loginForm.value;
    if (password !== confirmPassword) {
      this.loginForm.get('confirmPassword')?.setErrors({ mismatch: true });
      return;
    }

    this.loading = true;
    this.userAuthService.resetPassword(this.identifier, password!, code!)
      .subscribe({
        next: (response) => {
          this.loading = false;
          if (response.succeeded) {
            this.passwordResetState.clear();
            this.store.dispatch(UserAuthActions.ResetPasswordSuccess({ response: 'Your password was reset successfully. Please login with your new password.' }));
            this.router.navigate(['/portal-login']);
          } else {
            this.store.dispatch(UserAuthActions.ResetPasswordFailure({ error: response.errorMessages?.join(' ') || 'Unable to reset password. Please try again.' }));
          }
        },
        error: () => {
          this.loading = false;
          this.store.dispatch(UserAuthActions.ResetPasswordFailure({ error: 'Unable to reset password. Please try again.' }));
        }
      });
  }
}
